import { useVariant } from '@calendly/feature-check-react'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useGetUserQuery } from 'common'
import { isOnFreeStageSelector } from 'features/billing'
import { useFetchCalendarAccountsQuery, selectAll } from 'features/calendar_connections'
import { eventsCountSelector } from 'selectors/current_user'

/**
 * Hook to check targeting criteria for the Notetaker Free experiment.
 *
 * Targeting criteria checked in code:
 * - Free stage billing
 * - United States country
 * - English language
 * - Google or Outlook calendar connected
 * - Has had at least one meeting
 *
 * @returns {object} Object containing targeting status and loading state
 */
const useNotetakerFreeTargeting = () => {
  const { data: userData, isLoading: isUserLoading } = useGetUserQuery()
  const isOnFreeStage = useSelector(isOnFreeStageSelector)
  const eventsCount = useSelector(eventsCountSelector)
  const { data: calendarAccountsData, isLoading: isCalendarLoading } = useFetchCalendarAccountsQuery()

  const meetsTargetingCriteria = useMemo(() => {
    if (isUserLoading || isCalendarLoading) {
      return { isLoading: true, meetsTargeting: false }
    }

    if (!isOnFreeStage) {
      return { isLoading: false, meetsTargeting: false }
    }

    const countryCode = userData?.country_code
    if (countryCode !== 'US') {
      return { isLoading: false, meetsTargeting: false }
    }

    const language = userData?.locale
    if (language !== 'en') {
      return { isLoading: false, meetsTargeting: false }
    }

    const calendarAccounts = calendarAccountsData ? selectAll(calendarAccountsData) : []
    const hasGoogleCalendar = calendarAccounts.some((account) => account.kind === 'google')
    const hasOutlookCalendar = calendarAccounts.some((account) => account.kind === 'outlook')
    if (!hasGoogleCalendar && !hasOutlookCalendar) {
      return { isLoading: false, meetsTargeting: false }
    }

    if (!eventsCount || eventsCount < 1) {
      return { isLoading: false, meetsTargeting: false }
    }

    return { isLoading: false, meetsTargeting: true }
  }, [isUserLoading, isCalendarLoading, isOnFreeStage, userData, calendarAccountsData, eventsCount])

  return meetsTargetingCriteria
}

/**
 * Hook to check if the user meets all targeting criteria
 * and is in the Notetaker Free experiment variant.
 *
 * @returns {object} Object containing isInExperiment boolean and isLoading boolean
 *   - isLoading is needed by AiNotetakerWizard to prevent premature redirects
 */
export const useNotetakerFreeExperiment = () => {
  const { isLoading, meetsTargeting } = useNotetakerFreeTargeting()

  const shouldTrackExperiment = !isLoading && meetsTargeting
  const isInExperiment = useVariant('growth_notetaker_entry_points_free_users', shouldTrackExperiment) === 'variant'

  if (isLoading || !meetsTargeting) {
    return { isInExperiment: false, isLoading }
  }

  return { isInExperiment, isLoading }
}
